import { cn } from 'lib/utils'
import { Instagram, Linkedin, Mail, Music2, Youtube, type LucideIcon } from 'lucide-react'
import * as React from 'react'
import { FacebookIcon } from './FacebookIcon'
import { PinterestIcon } from './PinterestIcon'

export type Channel =
  'instagram' | 'facebook' | 'linkedin' | 'tiktok' | 'youtube' | 'pinterest' | 'x' | 'email'

interface ChannelMeta {
  label: string
  /** Brand color used for the lucide icon fallback. */
  color: string
  /** Optional gradient (Instagram). When set, the icon uses `background-clip: text` to render the brand gradient. */
  gradient?: string
  Icon: LucideIcon | React.FC<{ size?: number; className?: string }>
  /**
   * Full-color SVG asset (rounded-square brand badge with the symbol cut
   * out — same set used on the marketing site). Prefer this over `Icon`
   * wherever the surface can render an `<img>`; it ships the real Instagram
   * gradient, not a stroke approximation. Falls back to `Icon` when an
   * SVG asset doesn't exist.
   */
  iconSrc?: string
}

/**
 * Single source of truth for platform metadata. Brand colors come from the
 * social-platform table in foundations.md. Don't repeat these values
 * elsewhere — import `CHANNEL_META[channel]` instead.
 */
export const CHANNEL_META: Record<Channel, ChannelMeta> = {
  instagram: {
    label: 'Instagram',
    color: '#E4405F',
    gradient:
      'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
    Icon: Instagram,
    iconSrc: '/images/platforms/Instagram.svg',
  },
  facebook: {
    label: 'Facebook',
    color: '#1877F2',
    Icon: FacebookIcon,
    iconSrc: '/images/platforms/Facebook.svg',
  },
  linkedin: {
    label: 'LinkedIn',
    color: '#0A66C2',
    Icon: Linkedin,
    iconSrc: '/images/platforms/Linkedin.svg',
  },
  tiktok: {
    label: 'TikTok',
    color: '#000000',
    Icon: Music2,
    iconSrc: '/images/platforms/TikTok.svg',
  },
  youtube: {
    label: 'YouTube',
    color: '#FF0033',
    Icon: Youtube,
    iconSrc: '/images/platforms/Youtube.svg',
  },
  pinterest: {
    label: 'Pinterest',
    color: '#BD081C',
    Icon: PinterestIcon,
    iconSrc: '/images/platforms/Pinterest.svg',
  },
  x: { label: 'X', color: '#000000', Icon: Music2, iconSrc: '/images/platforms/X.svg' },
  email: { label: 'Email', color: '#8B92A0', Icon: Mail },
}

interface ChannelBadgeProps {
  channel: Channel
  /** `default` shows icon + label; `icon-only` shows only the platform icon as a small bubble. */
  variant?: 'default' | 'icon-only'
  /** Override the displayed label (e.g., "@takaai" instead of "Instagram"). */
  label?: string
  className?: string
}

/**
 * Mono platform badge — small pill with the channel's icon + name. Used in
 * post cards, calendar entries, queue cards, anywhere the user needs to know
 * "this content goes to X."
 *
 * The pill chrome is neutral T3 (paper-dim text on bg-3 with hairline
 * border); only the icon takes the platform's brand color so it pops without
 * stealing focus. Instagram is the one exception — its gradient is part of
 * the brand identity.
 *
 * Spec: playbook/design-system/components.md#channel-badge.
 *
 * Examples:
 *
 *   <ChannelBadge channel="instagram" />
 *   <ChannelBadge channel="email" label="Newsletter" />
 *   <ChannelBadge channel="linkedin" variant="icon-only" />
 */
export const ChannelBadge: React.FC<ChannelBadgeProps> = ({
  channel,
  variant = 'default',
  label,
  className,
}) => {
  const meta = CHANNEL_META[channel]
  const Icon = meta.Icon
  // Instagram gets the brand gradient on the icon via SVG fill (lucide
  // accepts a `style` prop that targets the path's fill via currentColor).
  // For other platforms, a flat brand color on the icon is cleaner.
  const iconStyle: React.CSSProperties = meta.gradient ? {} : { color: meta.color }

  if (variant === 'icon-only') {
    return (
      <span
        aria-label={meta.label}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full border border-line-bright bg-bg-3',
          className
        )}
      >
        <Icon size={12} className="shrink-0" style={iconStyle} />
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-line-bright bg-bg-3 text-paper-dim',
        'mono py-[5px] pr-[11px] pl-[8px] text-[11px]',
        className
      )}
    >
      <Icon size={12} className="shrink-0" style={iconStyle} />
      {label ?? meta.label}
    </span>
  )
}
